'use client';

import React, { FC } from 'react';
import { ComposerNoteImageLocalization } from '@osmosis/shared/types/learndb/learnData';
import { ComposerNotesArgsProps } from '@osmosis/shared/types/learndb/learnData';
import Image from 'next/image';
import { H1 } from '@osmosis/tailwind-ui/components/Text';
import { H2 } from '@osmosis/tailwind-ui/components/Text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { text0, text3 } from '@osmosis/tailwind-ui/styles/text';
import { P } from '@osmosis/tailwind-ui/components/Text';

import NotesComposerSections from './NotesComposerSections';
import { FormattedMessage } from 'react-intl';
import { useLanguageContext } from '../../../../context/LanguageProvider';
import { LanguageCode } from '@elsevier-health/osmosis-shared/languages';
import dynamic from 'next/dynamic';
import { bgPrimaryText } from '@osmosis/tailwind-ui/styles';

type Props = {
  notes: ComposerNotesArgsProps;
  hasAccess?: boolean;
  isDemoLearn?: boolean;
  disableImageViewers?: boolean;
  qrcodeSrc?: string;
};

const ImageViewer = dynamic(() => import('../../image-viewer/ImageViewer'), {
  ssr: false,
});

const filterLocalization = (
  localizationArray: ComposerNoteImageLocalization[],
): Record<LanguageCode, string> => {
  if (!localizationArray.length) return {} as Record<LanguageCode, string>;

  const filtered = localizationArray.reduce((obj, { image, countryCode }) => {
    const newObj = { ...obj } as Record<LanguageCode, string>;
    newObj[countryCode as LanguageCode] = image;
    return newObj;
  }, {});

  return filtered as Record<LanguageCode, string>;
};

/**
 * NotesComposerContent component.
 */
const NotesComposerContent: FC<Props> = ({
  notes,
  hasAccess,
  isDemoLearn,
  disableImageViewers = false,
  qrcodeSrc,
}) => {
  const {
    sections = [],
    image,
    title,
    titleImage,
    subtitle,
    altText,
    imageLocalizations = [],
    titleImageLocalizations = [],
    citations = [],
    theme,
  } = notes;

  const { activeLanguage } = useLanguageContext();

  const localizedImages = filterLocalization(imageLocalizations);
  const localizedTitleImages = filterLocalization(titleImageLocalizations);

  const disableViewerActions = isDemoLearn ? false : !hasAccess;

  const imageSrc = localizedImages?.[activeLanguage]
    ? localizedImages?.[activeLanguage]
    : image;

  const titleImageSrc = localizedTitleImages?.[activeLanguage]
    ? localizedTitleImages?.[activeLanguage]
    : titleImage;

  const themeColorBg: { [key: string]: string } = {
    purple: 'bg-purple-900',
    green: 'bg-green-800',
    teal: 'bg-teal-900',
    white: `${bgPrimaryText}`,
  };

  const themeColorText: { [key: string]: string } = {
    purple: 'text-purple-900',
    green: 'text-green-700',
    teal: 'text-teal-900',
  };

  return (
    <Box data-testid="notes-composer-content-wrapper" className={`w-full p-8`}>
      <Box
        className="flex flex-col gap-4"
        data-testid="notes-composer-content-container"
      >
        {title && (
          <>
            <Flex
              data-testid="notes-composer-content-title-container"
              className={`items-center sm:justify-between justify-center p-4 rounded-xl ${themeColorBg[theme]}`}
            >
              <Flex className="sm:w-20 w-0 h-[60px] justify-center">
                {titleImageSrc && (
                  <Box
                    className="sm:flex hidden"
                    data-testid="notes-composer-content-title-image"
                  >
                    <Image
                      alt={title}
                      width={40}
                      height={60}
                      src={titleImageSrc}
                    />
                  </Box>
                )}
              </Flex>
              <H1
                data-testid="notes-composer-content-title-text"
                className={`${text0}  print:${
                  theme !== 'white' ? themeColorText[theme] : 'text-primary-700'
                } text-center sm:text-3xl text-xl uppercase font-tannerscript font-bold`}
              >
                {title}
              </H1>
              <Box className="sm:flex hidden w-20 h-[60px]" />
            </Flex>
            {subtitle && (
              <H2
                className={`text-center my-4 ${themeColorText[theme]} uppercase font-tannerscript font-bold`}
                data-testid="notes-composer-content-subtitle-text"
              >
                {subtitle}
              </H2>
            )}
          </>
        )}

        <Box data-testid="notes-composer-content-main-image-wrapper">
          {imageSrc &&
            (disableImageViewers ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={imageSrc} alt={altText || ''} className="w-full" />
            ) : (
              <ImageViewer
                data-testid="notes-composer-content-main-image-viewer"
                altText={altText}
                src={imageSrc}
                disableActions={disableViewerActions}
                disableViewer={false}
              />
            ))}

          {!!sections?.length && (
            <NotesComposerSections
              theme={theme}
              themeText={
                theme !== 'white' ? themeColorText[theme] : 'text-primary-700'
              }
              sections={sections}
              disableViewerActions={disableViewerActions}
              disableImageViewers={disableImageViewers}
            />
          )}
        </Box>

        {citations.length > 0 ? (
          <Box
            className="italic text-center mb-5"
            data-testid="notes-citations"
          >
            {citations.map((citation) => (
              <Box key={`${citation.type}-${citation.name}`} className="mb-1">
                <FormattedMessage
                  id="citation.type.name"
                  defaultMessage="{type}: {name}"
                  values={{ type: citation.type, name: citation.name }}
                />
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>
      {qrcodeSrc && (
        <Box className="flex flex-col items-center gap-2">
          <P
            className={`${text3}`}
            data-testid="notes-composer-content-qrcode-text"
          >
            <FormattedMessage
              defaultMessage="Scan for related video and newest content."
              id="qrcode-message"
            />
          </P>
          <Image
            width={50}
            height={50}
            src={qrcodeSrc}
            alt="QR Code to learn page"
            data-testid="notes-composer-content-qrcode-image"
          />
        </Box>
      )}
    </Box>
  );
};

export default NotesComposerContent;
